import type { ContentItem } from '@/types/content';
import { blacklistItems } from '@/mocks/blacklist';
import { newsItems } from '@/mocks/news';

export const allContent: ContentItem[] = [...blacklistItems, ...newsItems];

export const allEvents: string[] = Array.from(
  new Set(allContent.map((item) => item.event)),
);

export function getAllContent(): ContentItem[] {
  return allContent;
}

export function getContentById(id: string): ContentItem | undefined {
  return allContent.find((item) => item.id === id);
}

export function getRelatedContent(item: ContentItem, limit = 3): ContentItem[] {
  const sameEvent = allContent.filter(
    (c) => c.id !== item.id && c.event === item.event,
  );
  const sameType = allContent.filter(
    (c) => c.id !== item.id && c.event !== item.event && c.type === item.type,
  );
  const others = allContent.filter(
    (c) => c.id !== item.id && c.event !== item.event && c.type !== item.type,
  );
  return [...sameEvent, ...sameType, ...others].slice(0, limit);
}

export function getLatestNews(limit = 5): ContentItem[] {
  return newsItems
    .slice()
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, limit);
}

export function getLatestBlacklist(limit = 6): ContentItem[] {
  return blacklistItems
    .slice()
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, limit);
}

export interface SearchOptions {
  type?: 'all' | 'blacklist' | 'news';
}

export function searchContent(query: string, options: SearchOptions = {}): ContentItem[] {
  const q = query.trim().toLowerCase();
  const type = options.type || 'all';
  return allContent.filter((item) => {
    if (type !== 'all' && item.type !== type) return false;
    if (!q) return true;
    const haystack = [
      item.title,
      item.summary,
      item.event,
      item.category,
      item.player || '',
      item.team || '',
      item.author,
      item.source,
      ...item.tags,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}

export interface BlacklistFilter {
  event?: string;
  dateFrom?: string;
  dateTo?: string;
  rankFrom?: number;
  rankTo?: number;
}

export function filterBlacklist(filter: BlacklistFilter): ContentItem[] {
  return blacklistItems.filter((item) => {
    if (filter.event && filter.event !== 'all' && item.event !== filter.event) {
      return false;
    }
    const day = item.date.slice(0, 10);
    if (filter.dateFrom && day < filter.dateFrom) return false;
    if (filter.dateTo && day > filter.dateTo) return false;
    if (typeof filter.rankFrom === 'number' && (item.rank || 0) < filter.rankFrom) {
      return false;
    }
    if (typeof filter.rankTo === 'number' && (item.rank || 0) > filter.rankTo) {
      return false;
    }
    return true;
  });
}

export function sortNews(
  items: ContentItem[],
  by: 'time' | 'hot',
): ContentItem[] {
  const list = items.slice();
  if (by === 'time') {
    return list.sort((a, b) => b.date.localeCompare(a.date));
  }
  return list.sort((a, b) => (b.readTime || 0) - (a.readTime || 0));
}

export function formatDate(date: string): string {
  return date.slice(0, 10);
}

export function formatTime(date: string): string {
  return date.slice(11, 16);
}

export function formatRankChange(change?: number): string {
  if (change === undefined) return '—';
  if (change > 0) return `+${change}`;
  if (change < 0) return `${change}`;
  return '0';
}